import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, switchMap } from "rxjs/operators";

import * as AdhocReportActions from '../actions/adhoc-report.actions';
import { AdhocReportService } from "@ptg-member/services/adhoc-report.service";
import { GetCardListForReportResponse } from "@ptg-member/types/models/adhoc-report.model";
import { of } from "rxjs";

@Injectable()
export class AdhocReportEffects {
    constructor(
        private actions$: Actions,
        private adhocReportService: AdhocReportService
    ) { }
    getCardList$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdhocReportActions.getCardListAction),
            switchMap(({ request }) => {
                return this.adhocReportService.getCardList(request).pipe(
                    map((response: GetCardListForReportResponse) => {
                        return AdhocReportActions.getCardListSuccessAction({ response });
                    }),
                    catchError((error) => {
                        return of(AdhocReportActions.getCardListFailAction({ error }));
                    })
                );
            })
        )
    );

    getDefaultFilter$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdhocReportActions.getDefaultFilterAction),
            switchMap(({ request }) => {
                return this.adhocReportService.getDefaultFilter(request).pipe(
                    map((response) => {
                        return AdhocReportActions.getDefaultFilterSuccessAction({ response });
                    }),
                    catchError((error) => {
                        return of(AdhocReportActions.getDefaultFilterFailAction({ error }));
                    })
                );
            })
        )
    );

    getDefaultTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdhocReportActions.getDefaultTemplateAction),
            switchMap(({ request }) => {
                return this.adhocReportService.getDefaultTemplate(request).pipe(
                    map((response) => {
                        return AdhocReportActions.getDefaultTemplateSuccessAction({ response });
                    }),
                    catchError((error) => {
                        return of(AdhocReportActions.getDefaultTemplateFailAction({ error }));
                    })
                );
            })
        )
    );

    getCardConfiguration$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdhocReportActions.getCardConfigurationAction),
            switchMap(({ request }) => {
                return this.adhocReportService.getCardConfiguration(request).pipe(
                    map((response) => {
                        return AdhocReportActions.getCardConfigurationSuccessAction({ response });
                    }),
                    catchError((error) => {
                        return of(AdhocReportActions.getCardConfigurationFailAction({ error }));
                    })
                );
            })
        )
    );

    getTemplates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdhocReportActions.getTemplatesAction),
            switchMap(() => {
                return this.adhocReportService.getTemplates().pipe(
                    map((response) => {
                        return AdhocReportActions.getTemplatesSuccessAction({ response });
                    }),
                    catchError((error) => {
                        return of(AdhocReportActions.getTemplatesFailAction({ error }));
                    })
                );
            })
        )
    );

    getTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdhocReportActions.getTemplateAction),
            switchMap(({ request }) => {
                return this.adhocReportService.getTemplate(request).pipe(
                    map((response) => {
                        return AdhocReportActions.getTemplateSuccessAction({ response });
                    }),
                    catchError((error) => {
                        return of(AdhocReportActions.getTemplateFailAction({ error }));
                    })
                );
            })
        )
    );

    getAdhocReport = createEffect(() =>
        this.actions$.pipe(
            ofType(AdhocReportActions.getAdhocReportAction),
            switchMap(({ request }) => {
                return this.adhocReportService.getAdhocReport(request).pipe(
                    map((response) => {
                        return AdhocReportActions.getAdhocReportSuccessAction({ response });
                    }),
                    catchError((error) => {
                        return of(AdhocReportActions.getAdhocReportFailAction({ error }));
                    })
                );
            })
        ) 
    );

    generateAdhocReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdhocReportActions.generateAdhocReportAction),
            switchMap(({ request }) => {
                return this.adhocReportService.generateAdhocReport(request).pipe(
                    map((response) => {
                        return AdhocReportActions.generateAdhocReportSuccessAction();
                    }),
                    catchError((error) => {
                        return of(AdhocReportActions.generateAdhocReportFailAction({ error }));
                    })
                );
            })
        )
    );
    
    createReportTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdhocReportActions.createReportTemplateAction),
            switchMap(({ request }) => {
                return this.adhocReportService.createReportTemplate(request).pipe(
                    map((response) => {
                        return AdhocReportActions.createReportTemplateSuccessAction();
                    }),
                    catchError((error) => {
                        return of(AdhocReportActions.createReportTemplateFailAction({ error }));
                    })
                );
            })
        )
    );

    updateReportTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdhocReportActions.updateReportTemplateAction),
            switchMap(({ request }) => {
                return this.adhocReportService.updateReportTemplate(request).pipe(
                    map((response) => {
                        return AdhocReportActions.updateReportTemplateSuccessAction();
                    }),
                    catchError((error) => {
                        return of(AdhocReportActions.updateReportTemplateFailAction({ error }));
                    })
                );
            })
        )
    );

    removeReportTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdhocReportActions.removeReportTemplateAction),
            switchMap(({ request }) => {
                return this.adhocReportService.removeReportTemplate(request).pipe(
                    map(() => {
                        return AdhocReportActions.removeReportTemplateSuccessAction();
                    }),
                    catchError((error) => {
                        return of(AdhocReportActions.removeReportTemplateFailAction({ error }));
                    })
                );
            })
        )
    );

    removeReportAuditTrail$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdhocReportActions.removeReportAuditTrailAction),
            switchMap(({ request }) => {
                return this.adhocReportService.removeReportAuditTrail(request).pipe(
                    map(() => {
                        return AdhocReportActions.removeReportAuditTrailSuccessAction();
                    }),
                    catchError((error) => {
                        return of(AdhocReportActions.removeReportAuditTrailFailAction({ error }));
                    })
                );
            })
        )
    );
}